<template>
  <Panel lineColor="var(--primary-color)">
    <template v-slot:header>
      <h3 class="panel-title">Notificações agendadas</h3>
    </template>
    <DataTable
      :headers="headers"
      :endpoint="endpoint"
      :filters="filters"
      sortBy="data_agendada"
      :sortDesc="true"
      ref="datatable"
      noHideFilters
    >
      <template v-slot:data_agendada="{ col }">
        {{ formatarDataHora(col) }}
      </template>

      <template v-slot:titulo="{ row, col }">
        <div class="text-truncate" style="max-width: 250px;" :title="row.titulo">
          {{ col }}
        </div>
      </template>

      <template v-slot:canal="{ col }">
        {{ textoCanal(col) }}
      </template>

      <template v-slot:tipo_destinatario="{ col }">
        {{ col === "todos" ? "Todos os clientes" : "Clientes selecionados" }}
      </template>

      <template v-slot:usuario_nome="{ col }">
        {{ col || "-" }}
      </template>

      <template v-slot:status="{ row }">
        <v-chip
          small
          dark
          :color="statusInfo(row.status).cor"
          :title="row.status === 'FALHA' ? row.erro : ''"
        >
          {{ statusInfo(row.status).texto }}
        </v-chip>
      </template>

      <template v-slot:acoes="{ row }">
        <v-btn
          icon
          small
          :disabled="!podeAlterar(row.status)"
          :title="tituloAcao('Editar ou reagendar', row.status)"
          @click.stop="editar(row)"
        >
          <v-icon small>fas fa-edit</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :disabled="!podeAlterar(row.status)"
          :title="tituloAcao('Cancelar agendamento', row.status)"
          @click.stop="pedirCancelamento(row)"
        >
          <v-icon small>fas fa-ban</v-icon>
        </v-btn>
      </template>
    </DataTable>

    <v-dialog v-model="dialogCancelar" max-width="420">
      <v-card>
        <v-card-title>Cancelar agendamento</v-card-title>
        <v-card-text>
          A notificação
          <strong>{{ agendamentoCancelar && agendamentoCancelar.titulo }}</strong>
          não será enviada. Deseja continuar?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <FormButton
            text="Voltar"
            type="auxiliar"
            :disabled="cancelando"
            @click="dialogCancelar = false"
          />
          <FormButton
            text="Cancelar agendamento"
            type="error"
            :loading="cancelando"
            @click="confirmarCancelamento"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </Panel>
</template>

<script>
import Panel from "@/components/Panel";
import DataTable from "@/components/DataTable";
import FormButton from "@/components/form/FormButton";
import { mapGetters } from "vuex";
import { NOTIFICACAO_PUSH_CANCELAR_AGENDAMENTO } from "@/store/actions/notificacaoPush";
import {
  CANAL_TEXTO,
  STATUS_AGENDAMENTO,
  MENSAGEM_EM_PROCESSAMENTO,
  statusAgendamento,
  podeAlterarAgendamento,
  formatarDataHoraBrasilia
} from "./agendamento-helpers";

export default {
  name: "AgendamentosNotificacaoPush",
  components: { Panel, DataTable, FormButton },
  data: () => ({
    dialogCancelar: false,
    agendamentoCancelar: null,
    cancelando: false,
    headers: [
      { text: "Agendado para", value: "data_agendada", width: "150px" },
      { text: "Título", value: "titulo", sortable: false },
      { text: "Canal", value: "canal", sortable: false },
      { text: "Público", value: "tipo_destinatario", sortable: false },
      { text: "Usuário", value: "usuario_nome", sortable: false },
      { text: "Status", value: "status", sortable: false, width: "130px" },
      {
        text: "Ações",
        value: "acoes",
        sortable: false,
        align: "center",
        width: "110px",
        clickable: false
      }
    ]
  }),
  computed: {
    ...mapGetters(["getFornecedorId"]),
    endpoint() {
      return `/api/web/internal/v2/marketing/push/agendamentos/${this.getFornecedorId}/lista`;
    },
    filters() {
      return [
        {
          label: "Status",
          query: "status",
          type: "select",
          clearable: true,
          options: Object.keys(STATUS_AGENDAMENTO).map(valor => ({
            value: valor,
            text: STATUS_AGENDAMENTO[valor].texto
          }))
        },
        {
          label: "Data Início",
          query: "dataInicio",
          type: "date",
          clearable: true
        },
        { label: "Data Fim", query: "dataFim", type: "date", clearable: true }
      ];
    }
  },
  methods: {
    statusInfo: statusAgendamento,
    podeAlterar: podeAlterarAgendamento,
    formatarDataHora: formatarDataHoraBrasilia,
    textoCanal(canal) {
      return CANAL_TEXTO[canal] || canal || "-";
    },
    tituloAcao(acao, status) {
      return podeAlterarAgendamento(status)
        ? acao
        : "Disponível apenas para notificações agendadas, antes do início do envio";
    },
    editar(row) {
      this.$router.push({
        path: "/fornecedor/marketing/push-notification",
        query: { agendamento: row.id }
      });
    },
    pedirCancelamento(row) {
      this.agendamentoCancelar = row;
      this.dialogCancelar = true;
    },
    confirmarCancelamento() {
      this.cancelando = true;
      this.$store
        .dispatch(
          NOTIFICACAO_PUSH_CANCELAR_AGENDAMENTO,
          this.agendamentoCancelar.id
        )
        .then(() => {
          this.$vueOnToast.pop("success", "Agendamento cancelado.");
        })
        .catch(erro => {
          this.$vueOnToast.pop(
            "error",
            erro && erro.status === 409
              ? erro.mensagem || MENSAGEM_EM_PROCESSAMENTO
              : (erro && erro.mensagem) || "Falha ao cancelar o agendamento."
          );
        })
        .finally(() => {
          this.cancelando = false;
          this.dialogCancelar = false;
          this.$refs.datatable.reload();
        });
    }
  }
};
</script>

<style scoped>
.panel-title {
  margin: 0;
  font-size: 1.2rem;
  color: var(--primary-color);
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
